import {useEffect} from 'react';
import {getAppearance} from './appearance-data';

/**
 * Applies theme-option values to the document as CSS custom properties (--ap-*).
 * Additive and namespaced (--ap-*) so it never collides with the existing
 * css_themes palette/font system. Blocks/header/footer can opt into these vars.
 *
 * IMPORTANT: fonts are owned by the existing theme system (admin/settings/themes
 * → `--be-font-family`). We must NOT set document.body.style.fontFamily here —
 * an inline body style overrides `--be-font-family` and silently breaks the
 * theme font picker. So this function deliberately does NOT touch fonts.
 */
export function applyThemeOptionVars(values: Record<string, any>): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const setVar = (k: string, v: any) => {
    if (v !== undefined && v !== null && v !== '') root.style.setProperty(k, String(v));
  };

  // ONLY vars that something actually consumes are set here. This used to also
  // emit --ap-primary / --ap-secondary / --ap-link / --ap-body-bg /
  // --ap-header-bg / --ap-button-color, none of which had a single consumer —
  // so the matching admin controls silently did nothing. The site palette is
  // owned by the css_themes `--be-*` variables (admin/settings/themes →
  // "Colors & Dark Mode"), which is what Tailwind resolves. Don't re-add a
  // parallel palette here; wire any new option to a real consumer first.
  setVar('--ap-header-text', values.header_text_color); // common.css navbar
  setVar('--ap-footer-text', values.footer_text_color); // common.css footer
  // --ap-footer-bg has no CSS consumer, but footer.tsx reads `footer_bg` from
  // the options object directly for non-simple footer styles.
  setVar('--ap-footer-bg', values.footer_bg);

  // Effects toggles. Default is ON for every effect — so we only ADD the
  // disabling class when the admin has EXPLICITLY turned it off (value ===
  // false). An unset/null option must NOT hide anything (that bug was hiding
  // the announcement block whenever announcement_bar was simply not set).
  const disableWhenFalse = (cls: string, val: any) => {
    root.classList.toggle(cls, val === false);
  };
  disableWhenFalse('ap-no-animations', values.animations);
  disableWhenFalse('ap-no-counters', values.counters);
  disableWhenFalse('ap-no-typing', values.typing_effect);
  disableWhenFalse('ap-no-announcements', values.announcement_bar);
  disableWhenFalse('ap-no-breadcrumbs', values.breadcrumbs);
  disableWhenFalse('ap-no-language-switcher', values.language_switcher);

  // Sticky-header: ADD the class only when explicitly enabled.
  root.classList.toggle('ap-sticky-header', values.sticky_header === true);
}

/**
 * Global component (mount once at app root) that applies the ACTIVE theme's
 * options on load. Renders nothing.
 */
export function AppearanceStyle() {
  useEffect(() => {
    const options = getAppearance().theme?.options;
    if (options && Object.keys(options).length) applyThemeOptionVars(options);
  }, []);
  return null;
}
